import {
  collectCricketMatches,
  formatOdds,
  kickoffTime,
  formatScoreline,
  resolveApiBase,
  SportsApiClient,
  type CricketFormat,
  type CricketMatch,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'
import { useTimeZone } from '../../use-time-zone'
import { matchCenterStyles } from '../match-center'

/**
 * The cricket match centre — the Flashscore/Cricbuzz reading of a cricket
 * fixture list, in the same shell as the other GoalServe centres (date bar,
 * series dropdown, "Resultados" toggle, 2-way odds with the per-locale brand).
 * Each match is two stacked team lines with per-innings scores ("245/6 (48.2)",
 * Tests "547/9d & 164/1 (24)"), a format badge, and a third line carrying the
 * result sentence, the in-play situation ("Día 2 · 3ª ent. · 24 ov · Khan RL
 * lead by 405 runs.") or, before the start, the venue and toss.
 *
 * Sources, merged by match id (first wins): `cricket/livescore` (today + every
 * in-progress multi-day match + yesterday's results), the `cricket-matches`
 * store replayed through `livescores?day=-7..-1` when results are on, and the
 * `cricket/schedule` fixture list for the coming fortnight. Odds resolve by id —
 * cricket's getodds ids ARE the livescore ids — with the name+date pool as the
 * gap filler, like every other centre.
 */
export const CRICKET_CENTER_ATTRIBUTES = [
  'competition',
  'format',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface Props extends WidgetProps {
  competition?: string
  format?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

interface MatchOdds {
  home: number | null
  draw: number | null
  away: number | null
}
interface BookmakerResponse {
  oddsFormat: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

/** Results window replayed from the store; upcoming window kept from the schedule. */
const RESULTS_DAYS = [-7, -6, -5, -4, -3, -2, -1]
const UPCOMING_DAYS = 14
const DAY_MS = 86_400_000

const FORMAT_LABEL: Record<CricketFormat, string> = { T20: 'T20', ODI: 'ODI', TEST: 'Test', '': '' }
const FORMAT_ATTR: Record<string, CricketFormat> = { t20: 'T20', odi: 'ODI', test: 'TEST' }

const PICKER_LOCALE: Record<string, string> = {
  en: 'en-GB', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-PT', pl: 'pl-PL',
  ru: 'ru-RU', el: 'el-GR', hr: 'hr-HR', sr: 'sr-RS', bs: 'bs-BA', lv: 'lv-LV',
  uk: 'en-GB', ie: 'en-IE', mx: 'es-MX', pe: 'es-PE',
}

export const cricketCenterStyles = `
  ${matchCenterStyles}
  /* Two stacked team lines (tennis-fx) plus a third, muted situation line.
     The shared .fx centres its children; cricket reads as a scorecard — names
     flush left, scorelines flush right, the situation line under them. */
  .ck-fx { gap: 0.2rem; align-items: stretch; }
  .ck-fx .tp { gap: 0.5rem; }
  .ck-fx .tp .side { display: flex; align-items: center; gap: 0.45rem; min-width: 0; flex: 1 1 auto; }
  .ck-fx .tp .side img { width: 18px; height: 18px; object-fit: contain; flex: none; }
  .ck-fx .tp .nm { color: var(--sports-fg); }
  .ck-fx .tp.lose .nm { color: var(--sports-muted); }
  .ck-fx .tp.win .nm { font-weight: 700; }
  .ck-fx .sc { flex: none; min-width: 0; text-align: right; font-weight: 600; color: var(--sports-fg); white-space: nowrap; }
  .ck-fx .tp.lose .sc { color: var(--sports-muted); font-weight: 500; }
  .ck-fx .tp.win .sc { font-weight: 700; }
  .ck-fx .sc.pending { color: var(--sports-muted); font-weight: 400; }
  .ck-note { font-size: 0.74rem; color: var(--sports-muted); line-height: 1.3; overflow: hidden; text-overflow: ellipsis; }
  .ck-note.result { color: var(--sports-fg); font-weight: 500; }
  .ck-note.live { color: var(--sports-live); font-weight: 600; }
  .fmt {
    display: inline-block; margin-top: 0.2rem; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.04em;
    color: var(--sports-muted); border: 1px solid var(--sports-border); border-radius: 4px; padding: 0.05rem 0.3rem;
  }
  .fmt.test { color: #7a5b00; border-color: #f5cf5b; background: #fde68a; }
  .fmt.odi { color: #1d4ed8; border-color: #bfdbfe; background: #dbeafe; }
  .fmt.t20 { color: #166534; border-color: #bbf7d0; background: #dcfce7; }
  .time .fmt { display: block; margin: 0.2rem auto 0; width: max-content; }
  .league .lg .fmt { margin: 0; }
  .fmt-select { min-width: 7rem; }
  @container (max-width: 540px) {
    .fmt-select { flex: 1 1 100%; }
  }
`

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}
/** GoalServe "dd.MM.yyyy" → sortable yyyy-mm-dd, or '' when not that shape. */
function dayKey(date?: string): string {
  if (!date || !/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(date)) return ''
  const [d, m, y] = date.split('.')
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}
function ddmmyyyy(key: string): string {
  const [y, m, d] = key.split('-')
  return y && m && d ? `${d}/${m}/${y}` : key
}
function utcKey(ms: number): string {
  const d = new Date(ms)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}
/** Feed kickoff (UTC) → ISO string, for the odds name+date pool. */
function isoKickoff(date: string, time: string): string | undefined {
  const k = dayKey(date)
  if (!k) return undefined
  const [hh = '00', mm = '00'] = (time || '').split(':')
  return `${k}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00Z`
}
/** Feed kickoff (UTC) → wall-clock time in the site's zone (see siteTimeZone). */
function localTime(date: string, time: string, lang: string, tz?: string): string {
  const iso = isoKickoff(date, time)
  return (iso && kickoffTime(new Date(iso), lang, tz)) || time
}
function bestOf(values: (number | null | undefined)[]): number | null {
  const present = values.filter((v): v is number => v != null && Number.isFinite(v))
  return present.length ? Math.min(...present) : null
}
function monthGrid(y: number, m: number): { key: string; day: number; inMonth: boolean }[] {
  const startDow = new Date(Date.UTC(y, m, 1)).getUTCDay()
  const start = Date.UTC(y, m, 1 - startDow)
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start + i * DAY_MS)
    cells.push({ key: utcKey(start + i * DAY_MS), day: d.getUTCDate(), inMonth: d.getUTCMonth() === m })
  }
  return cells
}
function monthLabel(y: number, m: number, lang: string): string {
  const s = new Date(Date.UTC(y, m, 1)).toLocaleDateString(PICKER_LOCALE[lang] ?? 'en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Which day of a multi-day match today is (1-based), or 0 when not a Test. */
function matchDay(m: CricketMatch, todayKey: string): number {
  if (m.format !== 'TEST') return 0
  const start = dayKey(m.date)
  if (!start) return 0
  const diff = Math.round((Date.parse(`${todayKey}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / DAY_MS)
  const end = dayKey(m.dateEnd ?? '')
  const span = end ? Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / DAY_MS) + 1 : 5
  return Math.min(Math.max(diff + 1, 1), Math.max(span, 1))
}

const ORDINAL_ES = ['', '1ª', '2ª', '3ª', '4ª']

/** Generic in-play statuses that say nothing a scoreline doesn't. */
const BARE_LIVE = /^(not covered live|live|in progress|in play|started)$/i

/**
 * "Día 2 · 3ª ent. · 24 ov · Khan RL lead by 405 runs." — whatever of the
 * day (Tests), current innings + overs, a descriptive status ("Stumps",
 * "Innings break") and the feed's note is known.
 */
function liveDetail(m: CricketMatch, todayKey: string, t: (k: string, f: string) => string): string {
  const parts: string[] = []
  const day = matchDay(m, todayKey)
  if (day) parts.push(`${t('day', 'Día')} ${day}`)
  const played = m.homeInnings.length + m.awayInnings.length
  const batting = m.homeInnings.length >= m.awayInnings.length ? m.homeInnings : m.awayInnings
  const current = batting[batting.length - 1]
  if (played) parts.push(`${ORDINAL_ES[played] ?? `${played}ª`} ${t('inningsAbbr', 'ent.')}`)
  if (current?.overs) parts.push(`${current.overs} ov`)
  if (m.status && !BARE_LIVE.test(m.status)) parts.push(m.status)
  if (m.note) parts.push(m.note)
  return parts.join(' · ')
}

interface SeriesGroup {
  name: string
  format: CricketFormat
  matches: CricketMatch[]
}
interface DateGroup {
  day: string
  count: number
  series: SeriesGroup[]
}

export function CricketCenter({ competition, format, lang = 'es', limit, heading, refresh, apiBase }: Props) {
  const base = resolveApiBase(apiBase)
  const refreshSeconds = number(refresh, 60)
  const max = number(limit, 20)
  const client = new SportsApiClient(base)
  const t = useLabels(base, lang)
  const tz = useTimeZone()
  const [showFinished, setShowFinished] = useState(false)
  const [shownLimit, setShownLimit] = useState(max)
  const [seriesFilter, setSeriesFilter] = useState<string | null>(null)
  const [formatFilter, setFormatFilter] = useState<CricketFormat>(FORMAT_ATTR[(format ?? '').trim().toLowerCase()] ?? '')
  const [fromKey, setFromKey] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const now0 = new Date()
  const [viewMonth, setViewMonth] = useState({ y: now0.getFullYear(), m: now0.getMonth() })
  const todayKey = utcKey(Date.now())
  const horizonKey = utcKey(Date.now() + UPCOMING_DAYS * DAY_MS)

  // Live first (authoritative), then stored result days, then the schedule; any
  // one source failing must not blank the others — a 404 for a day the store
  // has nothing for is normal, so every fetch degrades to null.
  const { data, error, loading } = useFeed(
    (signal) =>
      Promise.all([
        client.livescores<unknown>('cricket', 0, signal).catch(() => null),
        ...(showFinished
          ? RESULTS_DAYS.map((d) => client.livescores<unknown>('cricket', d, signal).catch(() => null))
          : []),
        client.feed<unknown>('cricket', 'schedule', signal).catch(() => null),
      ]),
    [base, showFinished],
    refreshSeconds,
  )
  const { data: display } = useFeed(
    (signal) => client.bookmaker<BookmakerResponse>(lang, 'match-center', signal),
    [base, lang],
    0,
  )
  const oddsFormat: OddsFormat = display?.oddsFormat ?? 'decimal'
  const brand = display?.brand ?? null
  const link = brand?.link ?? undefined

  const feeds = Array.isArray(data) ? data : []
  const allFailed = feeds.length > 0 && feeds.every((f) => f === null)
  const seen = new Set<string>()
  const merged: CricketMatch[] = []
  for (const payload of feeds) {
    for (const m of collectCricketMatches(payload)) {
      if (seen.has(m.id)) continue
      const k = dayKey(m.date)
      // The schedule runs six weeks out; keep the centre to the coming fortnight
      // (and never let a stale schedule row show a past date as upcoming).
      if (m.state === 'scheduled' && k && (k > horizonKey || (k < todayKey && !showFinished))) continue
      seen.add(m.id)
      merged.push(m)
    }
  }

  // A Test in its third day belongs under today, not under the day it started
  // (Cricbuzz lists in-play matches under the current date); the start day
  // still heads it once it is over or before it begins.
  const dayOf = (m: CricketMatch): string => {
    const start = dayKey(m.date)
    return m.state === 'live' && start && start < todayKey ? todayKey : start
  }
  const needle = competition?.trim().toLowerCase()
  const matchDays = new Set(merged.map(dayOf).filter(Boolean))
  const scoped = merged
    .filter((m) => !needle || m.series.toLowerCase().includes(needle))
    .filter((m) => showFinished || m.state !== 'finished')
    .filter((m) => !fromKey || dayOf(m) === fromKey)
  const formats = [...new Set(scoped.map((m) => m.format).filter(Boolean))] as CricketFormat[]
  const formatScoped = formatFilter ? scoped.filter((m) => m.format === formatFilter) : scoped
  const seriesNames = [...new Set(formatScoped.map((m) => m.series).filter(Boolean))].sort()
  const all = formatScoped
    .filter((m) => !seriesFilter || m.series === seriesFilter)
    // Within a day, in-play matches first, then by kickoff.
    .sort((a, b) =>
      `${dayOf(a)} ${a.state === 'live' ? 0 : 1} ${a.time}`.localeCompare(`${dayOf(b)} ${b.state === 'live' ? 0 : 1} ${b.time}`),
    )
  const shown = all.slice(0, shownLimit)
  const sortedDays = [...matchDays].sort()
  const stepDay = (dir: 1 | -1) => {
    if (!fromKey) {
      setFromKey(dir === 1 ? sortedDays[0] : sortedDays[sortedDays.length - 1])
    } else {
      const i = sortedDays.indexOf(fromKey)
      setFromKey(sortedDays[i + dir] ?? (dir === -1 ? null : fromKey))
    }
    setShownLimit(max)
  }

  const dateGroups: DateGroup[] = []
  for (const m of shown) {
    const dk = dayOf(m)
    let dg = dateGroups[dateGroups.length - 1]
    if (!dg || dg.day !== dk) {
      dg = { day: dk, count: 0, series: [] }
      dateGroups.push(dg)
    }
    dg.count += 1
    const name = m.series || '—'
    let sg = dg.series.find((s) => s.name === name && s.format === m.format)
    if (!sg) {
      sg = { name, format: m.format, matches: [] }
      dg.series.push(sg)
    }
    sg.matches.push(m)
  }

  const refs = shown
    .filter((m) => m.state !== 'finished')
    .map((m) => ({ id: m.id, home: m.home, away: m.away, dateUtc: isoKickoff(m.date, m.time) }))
  const refsKey = refs.map((r) => r.id).join(',')
  const { data: oddsMap } = useFeed(
    (signal) =>
      refs.length > 0
        ? client.resolveOdds<Record<string, MatchOdds>>('cricket_10', refs, signal)
        : Promise.resolve({} as Record<string, MatchOdds>),
    [base, refsKey],
    refreshSeconds,
  )

  const teamLogo = (id?: string, name?: string): string | undefined =>
    id ? `${base}/v1/logos/cricket/teams/${id}${name ? `?name=${encodeURIComponent(name)}` : ''}` : undefined

  const cell = (label: string, price: number | null | undefined, best: boolean) => {
    const text = price != null ? formatOdds(price, oddsFormat) : ''
    const cls = `cell${best && text ? ' best' : ''}${text ? '' : ' empty'}`
    const inner = (
      <>
        <span class="k">{label}</span>
        {text || '—'}
      </>
    )
    return link && text ? (
      <a class={cls} href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class={cls}>{inner}</span>
    )
  }
  const bookmaker = () => {
    if (!brand?.logo && !brand?.name) return <span class="bk" />
    const inner = brand.logo ? <img src={brand.logo} alt={brand.name ?? ''} /> : (brand.name ?? '')
    return link ? (
      <a class="bk" href={link} target="_blank" rel="sponsored noopener">
        {inner}
      </a>
    ) : (
      <span class="bk">{inner}</span>
    )
  }
  const badge = (f: CricketFormat) => (f ? <span class={`fmt ${f.toLowerCase()}`}>{FORMAT_LABEL[f]}</span> : null)

  const teamLine = (m: CricketMatch, side: 'home' | 'away') => {
    const name = side === 'home' ? m.home : m.away
    const innings = side === 'home' ? m.homeInnings : m.awayInnings
    const winner = side === 'home' ? m.homeWinner : m.awayWinner
    const decided = m.state === 'finished' && (m.homeWinner || m.awayWinner)
    const cls = `tp${decided ? (winner ? ' win' : ' lose') : ''}`
    const logo = teamLogo(side === 'home' ? m.homeId : m.awayId, name)
    const score = formatScoreline(innings)
    return (
      <div class={cls}>
        <span class="side">
          {logo && <img src={logo} alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />}
          <span class="nm">{name}</span>
        </span>
        {m.state === 'scheduled' ? null : (
          <span class={score ? 'sc' : 'sc pending'}>{score || (m.state === 'live' ? t('yetToBat', 'por batear') : '—')}</span>
        )}
      </div>
    )
  }

  const row = (m: CricketMatch) => {
    const live = m.state === 'live'
    const odds = oddsMap?.[m.id]
    // Limited-overs cricket is a 2-way market (the feed's "3Way Result" draw at
    // 25–50 is a tie price nobody wants in the cell); only a Test can be drawn.
    const threeWay = m.format === 'TEST' && odds?.draw != null
    const best = bestOf(threeWay ? [odds?.home, odds?.draw, odds?.away] : [odds?.home, odds?.away])
    const note =
      m.state === 'finished'
        ? m.result || m.status
        : live
          ? liveDetail(m, todayKey, t)
          : [m.venue, m.toss].filter(Boolean).join(' · ')
    return (
      <div class={live ? 'row live' : 'row'} key={m.id}>
        <span class="time">
          {live ? (
            <span class="st">{t('liveNow', 'En directo')}</span>
          ) : m.state === 'finished' ? (
            t('finished', 'Final')
          ) : (
            localTime(m.date, m.time, lang, tz)
          )}
          {badge(m.format)}
        </span>
        <div class="fx tennis-fx ck-fx">
          {teamLine(m, 'home')}
          {teamLine(m, 'away')}
          {note && <div class={`ck-note${m.state === 'finished' ? ' result' : live ? ' live' : ''}`}>{note}</div>}
        </div>
        {m.state === 'finished' ? (
          <span class="odds" />
        ) : (
          <span class="odds">
            {cell('1', odds?.home, odds?.home != null && odds?.home === best)}
            {threeWay && cell('X', odds?.draw, odds?.draw != null && odds?.draw === best)}
            {cell('2', odds?.away, odds?.away != null && odds?.away === best)}
          </span>
        )}
        {bookmaker()}
      </div>
    )
  }

  const liveCount = all.filter((m) => m.state === 'live').length

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{heading ?? 'Cricket'}</span>
        {liveCount > 0 && <span class="live-badge">● {liveCount}</span>}
      </div>

      <div class="bar">
        {formats.length > 1 && (
          <select
            class="lg-select fmt-select"
            value={formatFilter}
            onChange={(e) => {
              setFormatFilter((e.target as HTMLSelectElement).value as CricketFormat)
              setSeriesFilter(null)
            }}
          >
            <option value="">{t('allFormats', 'Todos los formatos')}</option>
            {(['T20', 'ODI', 'TEST'] as CricketFormat[])
              .filter((f) => formats.includes(f))
              .map((f) => (
                <option key={f} value={f}>
                  {FORMAT_LABEL[f]}
                </option>
              ))}
          </select>
        )}
        {seriesNames.length > 1 && (
          <select
            class="lg-select"
            value={seriesFilter ?? ''}
            onChange={(e) => setSeriesFilter((e.target as HTMLSelectElement).value || null)}
          >
            <option value="">{t('allSeries', 'Todas las series')}</option>
            {seriesNames.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
        <div class="datenav">
          <button class="nav" aria-label="prev" disabled={sortedDays.length === 0 || fromKey === sortedDays[0]} onClick={() => stepDay(-1)}>
            ‹
          </button>
          <button class="datebtn" onClick={() => setPickerOpen((o) => !o)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
            {fromKey ? ddmmyyyy(fromKey) : t('allDates', 'Todas las fechas')}
          </button>
          <button
            class="nav"
            aria-label="next"
            disabled={sortedDays.length === 0 || fromKey === sortedDays[sortedDays.length - 1]}
            onClick={() => stepDay(1)}
          >
            ›
          </button>
        </div>
        <label class="toggle">
          <input type="checkbox" checked={showFinished} onChange={(e) => setShowFinished((e.target as HTMLInputElement).checked)} />
          {t('results', 'Resultados')}
        </label>
        {pickerOpen && (
          <div class="picker">
            <div class="pk-head">
              <button onClick={() => setViewMonth((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))}>‹</button>
              <span>{monthLabel(viewMonth.y, viewMonth.m, lang)}</span>
              <button onClick={() => setViewMonth((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))}>›</button>
            </div>
            <div class="pk-grid pk-dow">
              {Array.from({ length: 7 }, (_, i) =>
                new Date(Date.UTC(2023, 0, 1 + i)).toLocaleDateString(PICKER_LOCALE[lang] ?? 'en-GB', { weekday: 'short', timeZone: 'UTC' }),
              ).map((w) => (
                <span key={w}>{w}</span>
              ))}
            </div>
            <div class="pk-grid">
              {monthGrid(viewMonth.y, viewMonth.m).map((c) => (
                <button
                  key={c.key}
                  class={`pk-day${c.inMonth ? '' : ' out'}${matchDays.has(c.key) ? ' has' : ''}${fromKey === c.key ? ' sel' : ''}`}
                  disabled={!matchDays.has(c.key)}
                  onClick={() => {
                    setFromKey(c.key)
                    setPickerOpen(false)
                    setShownLimit(max)
                  }}
                >
                  {c.day}
                </button>
              ))}
            </div>
            <button
              class="pk-all"
              onClick={() => {
                setFromKey(null)
                setPickerOpen(false)
              }}
            >
              {t('allDates', 'Todas las fechas')}
            </button>
          </div>
        )}
      </div>

      {loading && <div class="sports-note">{t('loading', 'Cargando…')}</div>}
      {!loading && (error || allFailed) && shown.length === 0 && (
        <div class="sports-note error">{t('matchesUnavailable', 'Los partidos no están disponibles ahora.')}</div>
      )}
      {!loading && !error && !allFailed && shown.length === 0 && (
        <div class="sports-note">
          {needle || seriesFilter ? t('noSeriesMatches', 'Sin partidos en esta serie.') : t('noMatches', 'Sin partidos este día.')}
        </div>
      )}

      {dateGroups.map((dg) => (
        <div key={dg.day}>
          <div class="date-head">
            {ddmmyyyy(dg.day)}
            <span class="count">{dg.count === 1 ? t('oneMatch', '1 partido') : `${dg.count} ${t('nMatches', 'partidos')}`}</span>
          </div>
          {dg.series.map((sg) => (
            <div key={`${sg.name}|${sg.format}`}>
              <div class="league">
                <span class="lg">
                  {badge(sg.format)}
                  {sg.name}
                </span>
              </div>
              {sg.matches.map((m) => row(m))}
            </div>
          ))}
        </div>
      ))}

      {all.length > shown.length && (
        <button class="more" onClick={() => setShownLimit((l) => l + max)}>
          {t('loadMore', 'Ver más')}
        </button>
      )}
    </div>
  )
}
